/* eslint-disable @typescript-eslint/prefer-promise-reject-errors -- isApiError fails otherwise */
import { useSearchParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ClientInferRequest } from "@ts-rest/core";

import { autoId } from "~/utils/auto-id";
import { TimeoutError, timeout } from "~/utils/timeout";
import type { apiContract } from "~/api/contract";
import { client } from "~/api/client";
import { useInvalidateProducts } from "~/api/products/hooks/use-invalidate-products";

import { ordersKeys } from "./orders";

type CreateRequest = ClientInferRequest<
  typeof apiContract.v1.pos.payments.create
>;

export function usePaymentCreate() {
  const queryClient = useQueryClient();
  const invalidateProducts = useInvalidateProducts();
  const [, setSearchParams] = useSearchParams();

  return useMutation({
    mutationFn: async (body: Omit<CreateRequest["body"], "serviceId">) => {
      const serviceId = autoId(10);

      setSearchParams({ serviceId, orderId: body.orderId });

      try {
        return await timeout(
          new Promise((resolve, reject) => {
            void client.v1.pos.payments
              .create({
                body: { ...body, serviceId }
              })
              .then((res) => {
                if (res.status === 201) {
                  resolve(res.body);
                } else {
                  reject(res);
                }
              });
          }),
          { ms: 300000 } // 5 minutes
        );
      } catch (error) {
        if (error instanceof TimeoutError) {
          // automatically poll for the transaction request after 5 minutes
          setSearchParams({ serviceId, orderId: body.orderId, poll: "true" });
        }
        throw error;
      }
    },
    onSuccess: (_, { orderId }) => {
      setTimeout(async () => {
        await queryClient.invalidateQueries({
          queryKey: ordersKeys.detail(orderId).queryKey
        });
        invalidateProducts(null);
      }, 150);
    },
    onError: async (_, { orderId }) => {
      await queryClient.invalidateQueries({
        queryKey: ordersKeys.detail(orderId).queryKey
      });
    }
  });
}
